<template>
  <MapHoverPopup :feature="hoveredFeature ?? null">
    <v-row dense>
      <v-col>
        <div style="display: flex; font-weight: bold">
          <vs-text truncate type="overline">
            {{ hoveredFeature?.properties?.DivisionName }}
          </vs-text>
          <vs-text type="overline"> : </vs-text>
          <vs-text truncate type="overline">{{
            hoveredFeature?.properties?.OrganizationName
          }}</vs-text>
        </div>
        <vs-text truncate type="intro" bold>{{ hoveredFeature?.properties?.Name }}</vs-text>
        <vs-text truncate type="subdued">{{
          hoveredFeature?.properties?.ResourceTypeName
        }}</vs-text>
      </v-col>
    </v-row>
    <v-row dense align="center">
      <v-col class="shrink pr-2">
        <v-icon>navigation</v-icon>
      </v-col>

      <v-col>
        <vs-text type="overline">Last GPS update by</vs-text>
        <vs-text type="subdued">{{ latestUpdatedInfo ?? 'No updates yet' }}</vs-text>
      </v-col>
    </v-row>

    <v-row dense align="center">
      <v-col class="shrink pr-2">
        <v-icon>account_circle</v-icon>
      </v-col>

      <v-col>
        <vs-text type="overline">Operated by</vs-text>
        <vs-text type="subdued">{{ operatorName || 'No field workers on duty' }}</vs-text>
        <v-row v-if="props.hoveredFeature?.properties?.OperatorName" no-gutters align="center">
          <v-col class="shrink mr-2">
            <v-icon x-small :color="onDutyColor">mdi-checkbox-blank-circle </v-icon>
          </v-col>
          <v-col>
            <vs-text type="subdued">{{ onDutySubtext }}</vs-text>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </MapHoverPopup>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { formatDateTime } from '@/utils/date-formatting'
import moment from 'moment/moment'
import { useGetters } from 'vuex-composition-helpers'
import type { HoveredMapFeature } from '../mapbox/MapHoverState'
import MapHoverPopup from '../mapbox/MapHoverPopup.vue'

const props = defineProps<{
  hoveredFeature?: HoveredMapFeature
}>()

const { getBySid } = useGetters('users', ['getBySid'])

const operatorName = computed<string | null>(() => {
  if (props.hoveredFeature?.properties?.OperatorSid) {
    return getBySid.value(props.hoveredFeature?.properties?.OperatorSid)?.name
  }
  return null
})

const lastLocationName = computed<string | null>(() => {
  if (props.hoveredFeature?.properties?.LastLocationUserSid) {
    return getBySid.value(props.hoveredFeature?.properties?.LastLocationUserSid)?.name
  }
  return null
})

const onDutyColor = computed<string>(() => {
  if (props.hoveredFeature?.properties?.OnDuty) {
    return 'teal accent-2'
  }
  return 'grey lighten-1'
})

const onDutySubtext = computed<string | null>(() => {
  if (!props.hoveredFeature?.properties?.OperatorName) return null
  if (props.hoveredFeature?.properties?.OnDuty) {
    return `On duty since ${formatDateTime(
      moment.utc(props.hoveredFeature?.properties?.OperatorOccuredAt).local(),
    )}`
  } else {
    return `Last on duty ${formatDateTime(
      moment.utc(props.hoveredFeature?.properties?.OperatorOccuredAt).local(),
    )}`
  }
})

const latestUpdatedInfo = computed<string | null>(() => {
  if (lastLocationName.value) {
    return `${lastLocationName.value} - ${moment
      .utc(props.hoveredFeature?.properties?.LastLocationGPSUpdatedTime)
      .local()
      .fromNow()}`
  }
  return null
})
</script>

<style scoped></style>
